import { createContext, useContext } from 'react';

/**
 * What the nav needs to know about a conversation it is about to interrupt:
 * whether one is running, and how to end it properly.
 *
 * A context rather than lifting `SessionView`'s state, because `SessionView`
 * is 500+ lines and this needs two fields of it. Two fields, no other traffic.
 */
export type LiveSession = {
  live: boolean;
  end: () => Promise<void>;
};

/**
 * `LiveSession` plus the setter that lets `SessionView` register itself. The
 * shell owns the state (`AppLayout`); the setter is how the screen that
 * knows about a conversation gets it there.
 */
export type LiveSessionValue = LiveSession & {
  set: (next: LiveSession) => void;
};

export const LiveSessionContext = createContext<LiveSessionValue>({
  live: false,
  end: async () => {},
  set: () => {},
});

export function useLiveSession(): LiveSessionValue {
  return useContext(LiveSessionContext);
}

/**
 * Whether a navigation may proceed.
 *
 * Split from the component so the decision is a test rather than something a
 * reviewer has to verify by reading a click handler. `confirm` is injected
 * for the same reason: `window.confirm` cannot be called under `node --test`.
 */
export function confirmLeave(live: boolean, confirm: () => boolean): boolean {
  if (!live) return true;
  return confirm();
}
